import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { attachedQuery, type AttachedCategory, type AttachedItem, type DeviceAttachedPage, type SpecPair } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Attached Devices a fisei - inventoree/devices/others/deviceAttached.php
 * cu gestul din device_workspace.js: cele patru categorii una langa alta
 * (Display / Keyboard / Mouse / Other), un patrat pe piesa cu starea si
 * chevronul (`is-open`), apoi istoria conectarilor pe aceeasi grila.
 */

export function DeviceAttached({ id }: ScreenProps) {
  const { data, error } = useQuery(attachedQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return <Attached page={data} />
}

function Attached({ page }: { page: DeviceAttachedPage }) {
  if (page.empty) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> The agent has not reported peripherals for this device yet.
      </div>
    )
  }

  return (
    <>
      <div className="dcol-grid">
        {page.categories.map((cat) => (
          <Category key={cat.key} cat={cat} />
        ))}
      </div>
      <div className="att-history-wrap">
        <h3 className="att-history-head">History</h3>
        <div className="dcol-grid att-history-grid">
          {page.categories.map((cat) => (
            <div key={cat.key} className="att-hist-col">
              <h4 className="att-hist-name">{cat.label}</h4>
              {cat.history.length === 0 ? (
                <p className="dcol-empty">No connect or disconnect recorded yet.</p>
              ) : (
                <ol className="att-history-list">
                  {cat.history.map((row, i) => (
                    <li key={i} className={`att-history-item att-history-${row.action}`}>
                      <span className="att-history-when">{row.when}</span>
                      <span className="att-history-action">{row.label}</span>
                      <span className="att-history-name">{row.name}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function Category({ cat }: { cat: AttachedCategory }) {
  return (
    <section className={`dcol${cat.bad > 0 ? ' has-problem' : ''}`} data-cat={cat.key}>
      <h3 className="dcol-head" title={cat.lead}>
        <i className={`fas ${cat.icon}`} aria-hidden="true"></i>
        <span className="dcol-name">{cat.label}</span>
        <span className="dcol-count">{cat.count}</span>
        {cat.bad > 0 && (
          <span className="dcol-bad" title={`${cat.bad} not working right now`}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
          </span>
        )}
      </h3>
      {cat.items.length > 0 ? (
        <div className="device-config device-attached dcol-cards">
          {cat.items.map((item, i) => (
            <Card key={`${item.typeId}-${item.title}-${i}`} item={item} />
          ))}
        </div>
      ) : (
        <p className="dcol-empty">None connected right now.</p>
      )}
    </section>
  )
}

function Card({ item }: { item: AttachedItem }) {
  const [open, setOpen] = useState(false)
  const cardClass = `dcf-card att-status-${item.statusKey}${item.statusKey === 'not_working' ? ' has-problem' : ''}${open ? ' is-open' : ''}`

  return (
    <section className={cardClass} data-type={item.typeId}>
      <header className="dcf-head" onClick={() => setOpen(!open)}>
        <span className="dcf-icon">
          <i className={`fas ${item.icon}`} aria-hidden="true"></i>
        </span>
        <span className="dcf-title" title={item.title}>
          {item.title}
        </span>
        <span className={`dcf-chip dcf-chip-status dcf-chip-${item.statusKey}`}>{item.statusLabel}</span>
        {item.conn !== '' && <span className="dcf-chip dcf-chip-conn">{item.conn}</span>}
        <i className="fas fa-chevron-down dcf-chevron" aria-hidden="true"></i>
      </header>

      <div className="dcf-summary">
        {item.kind !== '' && <div className="dcf-line dcf-muted">{item.kind}</div>}
        {item.detail !== '' && <div className="dcf-line dcf-warn">{item.detail}</div>}
        {item.brief.length > 0 && (
          <dl className="dcf-brief">
            {item.brief.map((p) => (
              <Pair key={p.name} pair={p} />
            ))}
          </dl>
        )}
      </div>

      <div className="dcf-details">
        <dl className="dcf-specs">
          {item.specs.map((p, i) => (
            <Pair key={i} pair={p} />
          ))}
        </dl>
      </div>
    </section>
  )
}

function Pair({ pair }: { pair: SpecPair }) {
  return (
    <>
      <dt>{pair.name}</dt>
      <dd>
        {pair.mark === 'dash' ? <span className="dcf-muted">&ndash;</span> : pair.mark === 'na' ? <em className="color-steel">n/a</em> : pair.value}
      </dd>
    </>
  )
}
